import { Autocomplete, Grid2, TextField } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string, type Schema } from 'yup';

import ContentLoader from '@components/ContentLoader';
import {
  FormikDropdownField,
  FormikTelField,
  FormikTextField,
} from '@components/FormikFields';
import InputLabel from '@components/InputLabel/index';
import RoundedButton from '@components/RoundedButton/index';

import {
  CARE_TEAM_ORG_TYPE_MAP,
  CARE_TEAM_ROLE_MAP,
  NAME_PREFIXES,
} from '@constants/care_team';
import { COUNTRIES } from '@constants/countries';
import { RELATED_PERSON_RELATIONSHIP_MAP } from '@constants/option-types/related-person';

import { useUpsertCareTeam } from '@hooks/api/care_team';
import {
  useCreateRelatedPerson,
  useUpdateRelatedPerson,
} from '@hooks/api/related-person';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { PatientContactType } from '@models/contact';
import { getRelatedPersonTemplate } from '@models/fhir-templates/related-person';
import type { FormValues, RelatedPersonRecord } from '@models/related-person';

import { capitalize } from '@utils/format';

import { ContactType, type ContactModel } from 'generated/types';

import { FormWrapper, SectionDivider, SectionLabel } from './ContactForm.style';
import FhirPractitionerSearch from './FhirPractitionerSearch';

const messages = defineMessages({
  firstName: {
    id: 'GLOBAL.firstName',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  invalidEmail: {
    id: 'GLOBAL.invalidEmail',
    defaultMessage: 'Invalid email',
  },
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  suite: {
    id: 'GLOBAL.suite',
    defaultMessage: 'Suite/Apartment',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  relationship: {
    id: 'contactForm.relationship',
    defaultMessage: 'Relationship',
  },
  careTeamRole: {
    id: 'contactForm.careTeamRole',
    defaultMessage: 'Care Team Role',
  },
  prefix: {
    id: 'contactForm.prefix',
    defaultMessage: 'Prefix',
  },
  contactType: {
    id: 'contactForm.contactType',
    defaultMessage: 'Contact Type',
  },
  orgType: {
    id: 'contactForm.orgType',
    defaultMessage: 'Organization Type',
  },
  orgName: {
    id: 'contactForm.orgName',
    defaultMessage: 'Organization Name',
  },
  sectionContact: {
    id: 'contactForm.sectionContact',
    defaultMessage: 'Contact',
  },
  sectionAddress: {
    id: 'contactForm.sectionAddress',
    defaultMessage: 'Address',
  },
  lengthValidation: {
    id: 'GLOBAL.formValidation.length',
    defaultMessage:
      '{input, select, undefined {Input} other {{input}}} must be longer than {count} characters',
  },
  lowercase: {
    id: 'GLOBAL.lowercaseEmail',
    defaultMessage: 'Email must be lowercase',
  },
  invalidPhoneFormat: {
    id: 'GLOBAL.invalidPhoneFormat',
    defaultMessage:
      'Phone number is not valid. Try including a country code (e.g. +1)',
  },
});

interface ContactFormProps {
  onClose?: () => void;
  /**
   * Existing related person to be edited
   */
  person?: RelatedPersonRecord;
  /**
   * Type of related person to be added (used for Legal/Directive section)
   */
  type?: string;
  /**
   * Contact type
   */
  contactType?: PatientContactType;
  /**
   * Existing contact (related person or practitioner in careteam) to be edited
   */
  contact?: ContactModel;
  /**
   * CareTeam.managingOrganization field - ensures we don't lose this field with updates
   */
  organization_ids?: string[];
}

export default function ContactForm({
  contact,
  contactType = 'related-person',
  onClose,
  organization_ids,
  person,
  type,
}: ContactFormProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [searchText, setSearchText] = useState<string>('');
  const {
    state: { activeProfile },
  } = useSession();
  const { emailSchema, phoneSchema } = useCommonSchemas();
  const patient_id = activeProfile?.patient_id;
  const careTeamRoleOptions = Array.from(CARE_TEAM_ROLE_MAP.entries())
    .map(([k, v]) => {
      return {
        role_code: k,
        role_name: v,
      };
    })
    .sort((a, b) => -b.role_name.localeCompare(a.role_name))
    .map(({ role_code }) => role_code);
  const relatedPersonRoleOptions = Array.from(
    RELATED_PERSON_RELATIONSHIP_MAP.entries()
  )
    .map(([k, v]) => ({ role_code: k, role_name: v }))
    .sort((a, b) => -b.role_name.localeCompare(a.role_name))
    .map(({ role_code }) => role_code);

  const [createRelatedPerson, createRelatedPersonLoading] =
    useCreateRelatedPerson();
  const [updateRelatedPerson, updateRelatedPersonLoading] =
    useUpdateRelatedPerson();
  const [upsertCareTeam, upsertCareTeamLoading] = useUpsertCareTeam();
  const loading =
    createRelatedPersonLoading ||
    updateRelatedPersonLoading ||
    upsertCareTeamLoading;

  const initialValues: FormValues = {
    contact_id: '',
    prefix: contact?.prefix ?? '',
    first_name: person?.first_name ?? contact?.first_name ?? '',
    last_name: person?.last_name ?? contact?.last_name ?? '',
    phone: person?.phone ?? contact?.phone ?? '',
    email: person?.email ?? contact?.email ?? '',
    address: person?.address ?? contact?.address ?? '',
    address2: person?.address2 ?? contact?.address2 ?? '',
    postalCode: person?.postalCode ?? contact?.postal_code ?? '',
    city: person?.city ?? contact?.city ?? '',
    region: person?.region ?? contact?.region ?? '',
    country: person?.country ?? contact?.country ?? '',
    association: person?.association ?? '',
    type: contact?.resourceType ?? null,
    org_type: contact?.org_type_code,
    roles: [
      ...(type && RELATED_PERSON_RELATIONSHIP_MAP.has(type)
        ? [
            {
              role_code: type,
              role_name: RELATED_PERSON_RELATIONSHIP_MAP.get(type) ?? type,
            },
          ]
        : []),
      ...(person?.relationship ?? []),
      ...(contact?.role_name && contact.role_code
        ? [
            {
              role_code: contact.role_code,
              role_name: contact.role_name,
            },
          ]
        : []),
    ],
    search: { filter: 'name', text: null, has_queried: false, no_match: null },
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    contact_id: string().nullable(),
    prefix: string(),
    first_name: string().when(
      ['type', 'search', 'contact_id'],
      ([type, search, contact_id]) => {
        if (type === ContactType.Organization) {
          return string();
        }
        if (type === ContactType.Practitioner) {
          if (search?.no_match || contact_id) {
            return string().required(formatMessage(messages.required));
          }
          return string();
        }
        return string().required(formatMessage(messages.required));
      }
    ),
    last_name: string().when(
      ['type', 'search', 'contact_id'],
      ([type, search, contact_id]) => {
        if (type === ContactType.Organization) {
          return string();
        }
        if (type === ContactType.Practitioner) {
          if (search?.no_match || contact_id) {
            return string().required(formatMessage(messages.required));
          }
          return string();
        }
        return string().required(formatMessage(messages.required));
      }
    ),
    phone: phoneSchema,
    email: emailSchema,
    address: string(),
    address2: string(),
    postalCode: string(),
    city: string(),
    region: string(),
    country: string(),
    association: string(),
    type:
      contactType === 'care-team'
        ? string()
            .oneOf([ContactType.Organization, ContactType.Practitioner])
            .required(formatMessage(messages.required))
        : string().nullable(),
    org_type: string().when('type', {
      is: (value: string | null | undefined) =>
        value === ContactType.Organization,
      then: () => string().required(formatMessage(messages.required)),
      otherwise: () => string().nullable(),
    }),
    roles: array()
      .of(
        object()
          .shape({
            role_name: string().required(formatMessage(messages.required)),
            role_code: string().required(formatMessage(messages.required)),
          })
          .required(formatMessage(messages.required))
      )
      .required(formatMessage(messages.required)),
    search: object()
      .shape({
        filter: string().nullable(),
        text: string().nullable(),
        has_queried: boolean().defined(),
      })
      .when('type', {
        is: (value: string | null | undefined) =>
          value === ContactType.Practitioner,
        then: (schema) =>
          schema
            .shape({
              text: string().when('filter', ([filter]) => {
                if (filter === 'email') {
                  return string()
                    .test(
                      'is-email',
                      formatMessage(messages.invalidEmail),
                      (value) => {
                        if (!value) return true;
                        return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
                          value
                        );
                      }
                    )
                    .lowercase(formatMessage(messages.lowercase))
                    .required(formatMessage(messages.required));
                }
                if (filter === 'phone') {
                  return string()
                    .matches(
                      /^\+[1-9]\d{9,14}$/,
                      formatMessage(messages.invalidPhoneFormat)
                    )
                    .required(formatMessage(messages.required));
                }
                return string()
                  .min(
                    3,
                    formatMessage(messages.lengthValidation, {
                      input: 'Filter',
                      count: 3,
                    })
                  )
                  .required(formatMessage(messages.required));
              }),
            })
            .required(formatMessage(messages.required)),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  async function handleSubmit(values: FormValues) {
    try {
      if (!patient_id) {
        throw new Error('No patient');
      }
      if (contactType === 'care-team') {
        const {
          address,
          address2,
          city,
          country,
          email,
          first_name,
          last_name,
          org_type,
          phone,
          postalCode,
          prefix,
          region,
          roles,
          type,
        } = values;
        const firstRole = roles.at(0);
        if (!firstRole) {
          throw new Error('No role selected.');
        }
        const { role_code, role_name } = firstRole;
        if (!type) {
          throw new Error('No care team contact type selected.');
        }

        await upsertCareTeam({
          input: {
            patient_id,
            organization_ids,
            participant: {
              id: contact?.id ?? (values.contact_id || undefined),
              prefix,
              first_name,
              last_name,
              phone,
              email,
              address,
              address2,
              city,
              region,
              country,
              role_code,
              role_name,
              postal_code: postalCode,
              type: type as ContactType,
              org_type_code: org_type,
              org_type_name: org_type
                ? CARE_TEAM_ORG_TYPE_MAP.get(org_type)
                : null,
            },
          },
        });
      }
      if (contactType === 'related-person') {
        const relatedPersonId = person?.id ?? contact?.id;
        if (relatedPersonId) {
          await updateRelatedPerson({
            input: {
              id: relatedPersonId,
              relatedPerson: {
                ...getRelatedPersonTemplate(patient_id, values),
              },
            },
          });
        } else {
          await createRelatedPerson({
            input: {
              ...getRelatedPersonTemplate(patient_id, values),
            },
          });
        }
      }
      onClose?.();
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    }
  }

  return (
    <ContentLoader lineCount={1} loading={loading}>
      <FormWrapper>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, isSubmitting, setFieldValue, values }) => {
            const isEditing = Boolean(person?.id ?? contact?.id);
            const isPractitionerCareTeamMember =
              contactType === 'care-team' &&
              values.type === ContactType.Practitioner;

            const displayFormFields =
              isPractitionerCareTeamMember && !isEditing
                ? values.search?.no_match
                : true;

            return (
              <Form>
                <Grid2 container spacing={1}>
                  {/* === Section 1: Identity === */}

                  {contactType === 'care-team' && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormikDropdownField
                        label={formatMessage(messages.contactType)}
                        name="type"
                        onChange={(value) => setFieldValue('type', value)}
                        options={[
                          ContactType.Organization,
                          ContactType.Practitioner,
                        ].map((ctType) => ({
                          k: ctType,
                          v: ctType,
                        }))}
                        required
                      />
                    </Grid2>
                  )}

                  {contactType === 'care-team' ? (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InputLabel
                        label={formatMessage(messages.careTeamRole)}
                        required
                      />
                      <Autocomplete
                        options={careTeamRoleOptions}
                        groupBy={(option) =>
                          CARE_TEAM_ROLE_MAP.get(option)
                            ?.at(0)
                            ?.toUpperCase() ?? ''
                        }
                        getOptionLabel={(option) =>
                          CARE_TEAM_ROLE_MAP.get(option) ?? ''
                        }
                        onChange={(_event, value) => {
                          if (value) {
                            setFieldValue('roles', [
                              {
                                role_code: value,
                                role_name: CARE_TEAM_ROLE_MAP.get(value),
                              },
                            ]);
                          }
                        }}
                        renderInput={(params) => (
                          <Field name="roles">
                            {({ field, meta }: FieldProps<string>) => (
                              <TextField
                                {...params}
                                {...field}
                                error={Boolean(meta.error)}
                                helperText={meta.touched ? meta.error : ''}
                                margin="dense"
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setSearchText(value);
                                }}
                                required
                                value={searchText}
                                sx={({ palette: { error } }) => ({
                                  ...(Boolean(meta.error) && {
                                    '& .MuiInputBase-root': {
                                      border: `1px solid ${error.main}`,
                                      borderRadius: '4px',
                                    },
                                  }),
                                })}
                              />
                            )}
                          </Field>
                        )}
                        size="small"
                        value={values.roles.at(0)?.role_code}
                      />
                    </Grid2>
                  ) : !type ? (
                    <Grid2 size={12}>
                      <InputLabel
                        label={formatMessage(messages.relationship)}
                        required
                      />
                      <Autocomplete
                        multiple
                        onChange={(_event, value) => {
                          const roles = value.map((val) => ({
                            role_code: val,
                            role_name: RELATED_PERSON_RELATIONSHIP_MAP.get(val),
                          }));
                          setFieldValue('roles', roles);
                        }}
                        getOptionLabel={(option) =>
                          capitalize(
                            RELATED_PERSON_RELATIONSHIP_MAP.get(option)
                          )
                        }
                        options={relatedPersonRoleOptions}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            helperText={
                              errors.roles
                                ? formatMessage(messages.required)
                                : ''
                            }
                            margin="dense"
                            sx={({ palette: { error } }) => ({
                              ...(Boolean(errors.roles) && {
                                '& .MuiInputBase-root': {
                                  border: `1px solid ${error.main}`,
                                  borderRadius: '4px',
                                },
                              }),
                            })}
                          />
                        )}
                        size="small"
                        value={values.roles.map(({ role_code }) => role_code)}
                      />
                    </Grid2>
                  ) : null}

                  {values.type === ContactType.Organization && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <FormikDropdownField
                        label={formatMessage(messages.orgType)}
                        name="org_type"
                        onChange={(value) => {
                          setFieldValue('org_type', value);
                        }}
                        options={Array.from(
                          CARE_TEAM_ORG_TYPE_MAP.entries()
                        ).map(([k, v]) => ({ k, v }))}
                        required
                      />
                    </Grid2>
                  )}

                  {isPractitionerCareTeamMember && !isEditing && (
                    <FhirPractitionerSearch />
                  )}

                  {displayFormFields && (
                    <>
                      {isPractitionerCareTeamMember && (
                        <Grid2 size={{ xs: 12, sm: 2 }}>
                          <FormikDropdownField
                            label={formatMessage(messages.prefix)}
                            name="prefix"
                            onChange={(value) => setFieldValue('prefix', value)}
                            options={NAME_PREFIXES.map((prefix) => ({
                              k: prefix,
                              v: prefix,
                            }))}
                          />
                        </Grid2>
                      )}
                      <Grid2
                        size={{
                          xs: 12,
                          sm: isPractitionerCareTeamMember ? 5 : 6,
                        }}
                      >
                        <FormikTextField
                          label={formatMessage(
                            values.type === ContactType.Organization
                              ? messages.orgName
                              : messages.firstName
                          )}
                          name="first_name"
                          required
                        />
                      </Grid2>
                      {values.type !== ContactType.Organization && (
                        <Grid2
                          size={{
                            xs: 12,
                            sm: isPractitionerCareTeamMember ? 5 : 6,
                          }}
                        >
                          <FormikTextField
                            label={formatMessage(messages.lastName)}
                            name="last_name"
                            required
                          />
                        </Grid2>
                      )}
                    </>
                  )}

                  {/* === Section 2: Contact === */}
                  {displayFormFields && (
                    <>
                      <Grid2 size={12}>
                        <SectionDivider>
                          <SectionLabel>
                            {formatMessage(messages.sectionContact)}
                          </SectionLabel>
                        </SectionDivider>
                      </Grid2>

                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikTelField
                          label={formatMessage(messages.phone)}
                          name="phone"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikTextField
                          label={formatMessage(messages.email)}
                          name="email"
                          type="email"
                        />
                      </Grid2>
                    </>
                  )}

                  {/* === Section 3: Address === */}
                  {displayFormFields && (
                    <>
                      <Grid2 size={12}>
                        <SectionDivider>
                          <SectionLabel>
                            {formatMessage(messages.sectionAddress)}
                          </SectionLabel>
                        </SectionDivider>
                      </Grid2>

                      <Grid2 size={{ xs: 12, sm: 8 }}>
                        <FormikTextField
                          label={formatMessage(messages.address)}
                          name="address"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormikTextField
                          label={formatMessage(messages.suite)}
                          name="address2"
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikTextField
                          label={formatMessage(messages.city)}
                          name="city"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikDropdownField
                          label={formatMessage(messages.country)}
                          name="country"
                          onChange={(value) => {
                            setFieldValue('country', value);
                          }}
                          options={COUNTRIES.map(({ code, name }) => ({
                            k: code,
                            v: name,
                          }))}
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikTextField
                          label={formatMessage(messages.postalCode)}
                          name="postalCode"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormikDropdownField
                          label={formatMessage(messages.region)}
                          name="region"
                          onChange={(value) => {
                            setFieldValue('region', value);
                          }}
                          options={COUNTRIES.find(
                            (c) => c.code === values.country || !values.country
                          )?.provinces.map((province) => ({
                            k: province,
                            v: province,
                          }))}
                        />
                      </Grid2>
                    </>
                  )}

                  {/* === Submit === */}
                  {(!isPractitionerCareTeamMember ||
                    isEditing ||
                    values.contact_id ||
                    values.search?.no_match) && (
                    <Grid2 size={12}>
                      <RoundedButton disabled={isSubmitting} type="submit">
                        {formatMessage(messages.submit)}
                      </RoundedButton>
                    </Grid2>
                  )}
                </Grid2>
              </Form>
            );
          }}
        </Formik>
      </FormWrapper>
    </ContentLoader>
  );
}
